import React, { useState } from "react";
import TextInputField from "../../../utils/Fields/TextInputField";
import { useDropzone } from "react-dropzone";
import { FooterButton } from "../Components/curatorApprovalStyle";
import ImageList from "../../Administration/Screens/LandingPageHero.js/ImageList";
import {
  Box,
  Typography,
  useMediaQuery,
  TextField,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";

import useThemeRender from "../../../theme/useThemeRender";
import { getImagePreview } from "../../../utils/function";
import DropZone from "../../../utils/Upload/Dropzone";
import { useSelector } from "react-redux";
import FileView from "../../Administration/FileView/FileView";
import { showErrorToast } from "../../../utils/Toast/toast";

const YearlyArtifactDocumentComponent = ({
  register,
  artifactManagementArray,
  onSave,
  showAddMoreArtificats,
  contentType,
  isUploading = false,
  documentUploadPct = null,
  thumbnailUploadPct = null,
  onCancelUpload,
  onAbortDocumentUpload,
  onAbortThumbnailUpload,
}) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isDarkTheme = useSelector((state) => state.theme);
  const { flagUser } = useSelector((store) => store.user);
  const [isDocumentDropped, setIsDocumentDropped] = useState(false);
  const [isThumbnailDropped, setIsThumbnailDropped] = useState(false);
  const [selectedDocumentImage, setSelectedDocumentImage] = useState(null);
  const [selectedThumbnailImage, setSelectedThumbnailImage] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const disabled =
    ((contentType === "LINK" || !selectedDocumentImage) &&
      !documentFile?.fileUrl?.trim()) ||
    !selectedThumbnailImage;

  // A file that's still actively uploading can still be cancelled — that's the point of
  // the delete button during a big upload. Only lock it once it has already finished and
  // is just waiting on its sibling: touching it then would swap in a new file locally
  // while the save already in flight keeps using the old one.
  const documentControlsDisabled = isUploading && documentUploadPct === null;
  const thumbnailControlsDisabled = isUploading && thumbnailUploadPct === null;

  const getArticatesVersionNumber = () => {
    return Math.round(artifactManagementArray?.[0].versionNumber) + 1;
  };

  const onDropDocument = (acceptedFiles) => {
    console.log(acceptedFiles);
    if (!acceptedFiles || acceptedFiles.length === 0 || !acceptedFiles[0]) {
      return;
    }
    const newImage = acceptedFiles[0];
    let imageWithPreview = getImagePreview(newImage);
    const updatedImage = [imageWithPreview];
    setDocumentFile(newImage);
    setSelectedDocumentImage(updatedImage);
    setIsDocumentDropped(true);
  };

  const onDropThumb = (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0 || !acceptedFiles[0]) {
      return;
    }
    const newImage = acceptedFiles[0];
    if (!newImage || !(newImage instanceof File || newImage instanceof Blob)) {
      return;
    }
    // Revoke old preview URL if it exists to prevent memory leaks
    if (selectedThumbnailImage?.[0]?.preview && selectedThumbnailImage[0].preview.startsWith('blob:')) {
      URL.revokeObjectURL(selectedThumbnailImage[0].preview);
    }
    const imageWithPreview = Object.assign(newImage, {
      preview: URL.createObjectURL(newImage),
    });
    const updatedImage = [imageWithPreview];
    setImageFile(newImage);
    setSelectedThumbnailImage(updatedImage);
    setIsThumbnailDropped(true);
  };

  const { getRootProps, getInputProps } = useDropzone({
    ...(contentType === "VIDEO"
      ? {
          accept: { "video/*": [] },
        }
      : {}),
    onDrop: onDropDocument,
    maxFiles: 1,
  });

  const { getRootProps: getRootPropsThumb, getInputProps: getInputPropsThumb } =
    useDropzone({
      accept: { "image/*": [] },
      onDrop: onDropThumb,
      maxFiles: 1,
    });

  // A file mid-upload has no local state to just clear — the request needs aborting.
  // Deleting one file only cancels that one; the other, if still uploading, keeps going.
  const handleDeleteDocument = async () => {
    if (documentUploadPct !== null) {
      onAbortDocumentUpload?.();
    }
    setDocumentFile(null);
    setSelectedDocumentImage(null);
    setIsDocumentDropped(false);
  };
  const handleDeleteThumb = async (docType) => {
    if (thumbnailUploadPct !== null) {
      onAbortThumbnailUpload?.();
    }
    setImageFile(null);
    setSelectedThumbnailImage(null);
    setIsThumbnailDropped(false);
  };
  const renderUploadProgress = (pct) => (
    <Box sx={{ mt: 1 }}>
      <LinearProgress
        variant={typeof pct === "number" ? "determinate" : "indeterminate"}
        value={typeof pct === "number" ? pct : undefined}
      />
      {typeof pct === "number" && (
        <Typography sx={{ fontSize: "10px", mt: 0.5 }}>
          {`Uploading… ${pct}%`}
        </Typography>
      )}
    </Box>
  );

  const handelClickSave = () => {
    const maxFileSizeMB = flagUser?.maxFileSize ?? 250;
    if (documentFile?.size > maxFileSizeMB * 1024 * 1024) {
      showErrorToast(`File size exceeds the ${maxFileSizeMB} MB limit. Please upload a smaller file.`);
      return;
    }else{
      onSave([[documentFile], [imageFile]], getArticatesVersionNumber())
    }
  }
  return (
    <Box
      sx={{
        background: useThemeRender("cardBackgroundColorLayer2"),
      }}
    >
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: "16px",
            padding: "16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              width: { sm: "100%", md: "360px" },
            }}
          >
            {contentType === "LINK" ? (
              <>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    mt: 1,
                    mb: 0.5,
                    fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                  }}
                >
                  Link
                </Typography>
                <TextField
                  placeholder="Paste Link"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-input": {
                      padding: "0px 8px",
                      lineHeight: "20px",
                      fontSize: "14px",
                      height: "32px",
                    },
                  }}
                  value={documentFile?.fileUrl ?? ""}
                  onChange={(e) => setDocumentFile({ fileUrl: e.target.value })}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <LinkIcon sx={{ fontSize: "16px" }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </>
            ) : (
              <Box
                sx={{
                  width: "408px",
                  maxWidth: "100%",
                  height: isDocumentDropped ? "auto" : "186px",
                  top: "16px",
                  left: "16px",
                  gap: "18px",
                  borderRadius: " var(--Cornerradiuseffect-corner-2)",
                }}
              >
                {isDocumentDropped ? (
                  <FileView
                    fileDocument={documentFile}
                    onDelete={handleDeleteDocument}
                    onPreview={() => {}}
                    disabled={documentControlsDisabled}
                  />
                ) : (
                  <DropZone
                    getRootProps={getRootProps}
                    getInputProps={getInputProps}
                    isDarkTheme={isDarkTheme}
                    label="Document"
                    uploadImageLabel="Upload File"
                    isRequired
                  />
                )}
                {documentUploadPct !== null && renderUploadProgress(documentUploadPct)}
              </Box>
            )}
            <Box
              sx={{
                width: "408px",
                maxWidth: "100%",
                height: "186px",
                top: "16px",
                left: "16px",
                paddingTop: "42px",
                borderRadius: " var(--Cornerradiuseffect-corner-2)",
              }}
            >
              {isThumbnailDropped ? (
                <ImageList
                  images={[...selectedThumbnailImage]}
                  onDragEnd={() => {}} // No-op since we only have one thumbnail
                  onDelete={handleDeleteThumb}
                  onReplace={(updatedImages) => {
                    if (updatedImages?.[0]?.file) {
                      onDropThumb([updatedImages[0].file]);
                    }
                  }}
                  disabled={thumbnailControlsDisabled}
                />
              ) : (
                <DropZone
                  getRootProps={getRootPropsThumb}
                  getInputProps={getInputPropsThumb}
                  isDarkTheme={isDarkTheme}
                  label="Thumbnail"
                  informativeText="Drag and drop your file here (only .jpg,.png)"
                  uploadImageLabel="Upload Image"
                  isRequired
                />
              )}
              {thumbnailUploadPct !== null && renderUploadProgress(thumbnailUploadPct)}
            </Box>
            <Box
              sx={{
                paddingTop: "42px",
              }}
            >
              <TextInputField
                label="Version Number"
                type="number"
                errors={false}
                register={register}
                name="newVersionNumber"
                value={getArticatesVersionNumber()}
                width={!isMobile ? 250 : "100%"}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            height: "32px",
            padding: "0px 16px 0px 16px",
            gap: "12px",
            marginBottom: "12px",
            justifyContent: isMobile ? "space-between" : "end",
          }}
        >
          <FooterButton
            sx={{
              background: "",
              color: "#005CBC",
              border: "1px solid var(--Tokens-Button-Outlined, #005CBC)",
              flex: isMobile && 1,
            }}
            onClick={() =>
              isUploading && onCancelUpload
                ? onCancelUpload()
                : showAddMoreArtificats(false)
            }
          >
            Cancel
          </FooterButton>
          <FooterButton
            sx={{
              background: disabled || isUploading ? "#A0A0A0" : "#005CBC",
              flex: isMobile && 1,
            }}
            disabled={disabled || isUploading}
            onClick={() => handelClickSave()}
          >
            {isUploading ? "Uploading…" : "Save"}
          </FooterButton>
        </Box>
      </>
    </Box>
  );
};

export default YearlyArtifactDocumentComponent;
